/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { ServicesClientImpl } from '../interactors';
import { Log } from '../logging';
import { NetworkManager } from '../network/NetworkManager';
import { ITivoSubscription } from '../tivo';
import { TivoSubscriptionModel } from '../tivo/TivoSubscriptionModel';
import { PlaybackRestrictionType } from './enums';
import { InHomeLocationRestrictionImpl } from './impls/InHomeLocationRestrictionImpl';
import { MetadataRestrictionImpl } from './impls/MetadataRestrictionImpl';
import { IMetadata, IPlayable, IRestrictionManager } from './interfaces';

export class RestrictionManager implements IRestrictionManager {
    private readonly TAG: string = 'RestrictionManager';

    private mRestrictionType: PlaybackRestrictionType;

    private oInhomeLocationRestrictionChecker: InHomeLocationRestrictionImpl;

    private oMetadataRestrictionChecker: MetadataRestrictionImpl;

    private oTivoSubscription: ITivoSubscription<PlaybackRestrictionType>;

    private static inHomeLocationInRecovery: boolean = false;

    public constructor() {
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange, false);
        document.addEventListener('visibilitychange', this.handleVisibilityChange, false);
        NetworkManager.subscribe(this.handleNetworkChange.bind(this));
        this.mRestrictionType = PlaybackRestrictionType.NO_RESTRICTION;
        this.oInhomeLocationRestrictionChecker = new InHomeLocationRestrictionImpl();
        this.oMetadataRestrictionChecker = new MetadataRestrictionImpl();
        this.oTivoSubscription = new TivoSubscriptionModel<PlaybackRestrictionType>();
    }

    private checkRestriction = (
        playable: IPlayable,
        metadata?: IMetadata,
    ): PlaybackRestrictionType => {
        if (NetworkManager.isNetworkDisconnected()) {
            this.mRestrictionType = PlaybackRestrictionType.NETWORK_DISCONNECTED;
            return this.mRestrictionType;
        }
        const inHomeLocationRestriction = this.oInhomeLocationRestrictionChecker.checkRestriction({
            playbackCOntentType: playable.playbackContentType,
            stationPolicy: playable?.channel?.policyInfo,
        });

        /**
         * according to the usecase, if service is inhome location service is unreachable,
         * check for other restrictions. if no other restriction is found, send restriction
         * as INHOME_LOCATION_UNREACHABLE. to achive that this.mRestrictionType is assigned
         * the value of inHomeLocationRestriction after checking for all other restrictions.
         */

        if (inHomeLocationRestriction === PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE) {
            if (!RestrictionManager.inHomeLocationInRecovery) {
                RestrictionManager.inHomeLocationInRecovery = true;
                const services = new ServicesClientImpl();
                services.recoverInHomeLocationInfo().then(() => {
                    RestrictionManager.inHomeLocationInRecovery = false;
                    this.handleInhomeLocationRecovery(playable, metadata);
                });
            }
        }

        if (
            inHomeLocationRestriction !== PlaybackRestrictionType.NO_RESTRICTION &&
            inHomeLocationRestriction !== PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE
        ) {
            this.mRestrictionType = inHomeLocationRestriction;
            return this.mRestrictionType;
        }

        this.mRestrictionType = this.oMetadataRestrictionChecker.checkRestriction({
            playable,
            metadata,
        });

        if (this.mRestrictionType !== PlaybackRestrictionType.NO_RESTRICTION) {
            return this.mRestrictionType;
        }

        /**
         * inHomeLocationRestriction at this point will be either
         * INHOME_LOCATION_UNREACHABLE || NO_RESTRICTION, so return
         */
        this.mRestrictionType = inHomeLocationRestriction;
        return this.mRestrictionType;
    };

    private handleInhomeLocationRecovery(playable: IPlayable, metadata?: IMetadata) {
        this.mRestrictionType = this.checkRestriction(playable, metadata);
        this.oTivoSubscription.notify(this.mRestrictionType);
    }

    private handleNetworkChange() {
        this.oTivoSubscription.notify(
            NetworkManager.isNetworkDisconnected()
                ? PlaybackRestrictionType.NETWORK_DISCONNECTED
                : PlaybackRestrictionType.NETWORK_CONNECTED,
        );
    }

    private handleVisibilityChange() {
        if (document.hidden) {
            this.mRestrictionType = PlaybackRestrictionType.APP_BACKGROUND;
        } else {
            this.mRestrictionType = PlaybackRestrictionType.APP_FOREGROUND;
        }
        this.log(
            'debug',
            'handleVisibilityChange: ',
            PlaybackRestrictionType[this.mRestrictionType],
        );
        this.oTivoSubscription.notify(this.mRestrictionType);
    }

    private log(
        level: 'debug' | 'info' | 'error',
        message: string,
        ...optionalParams: any[]
    ): void {
        Diagnostics[level](this.TAG, message, ...optionalParams);
        Log[level[0]](this.TAG, message, ...optionalParams);
        if (level === 'error') Diagnostics.error(this.TAG, 'stacktrace:,', optionalParams[0]);
    }

    public checkAllRestriction(playable: IPlayable, metadata: IMetadata): PlaybackRestrictionType {
        return this.checkRestriction(playable, metadata);
    }

    public checkCanPlayStartRestriction(playable: IPlayable): PlaybackRestrictionType {
        return this.checkRestriction(playable);
    }

    public getRestrictionListener(): ITivoSubscription<PlaybackRestrictionType> {
        return this.oTivoSubscription;
    }

    public getRestrictionType(): PlaybackRestrictionType {
        return this.mRestrictionType;
    }
}
